import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';


const OwnerIntakeForm = ({ onClose, onSubmit, editData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    nationalId: '',
    bankAccount: '',
    address: '',
    notes: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData?.name || '',
        phone: editData?.phone || '',
        email: editData?.email || '',
        nationalId: editData?.nationalId || '',
        bankAccount: editData?.bankAccount || '',
        address: editData?.address || '',
        notes: editData?.notes || ''
      });
    }
  }, [editData]);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (formData?.name || formData?.phone || formData?.email) {
        handleAutoSave();
      }
    }, 3000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData]);

  const handleAutoSave = () => {
    localStorage.setItem('ownerFormDraft', JSON.stringify(formData));
    setLastSaved(new Date());
  };

  const handleSave = () => {
    setIsSaving(true);
    handleAutoSave();
    setTimeout(() => {
      setIsSaving(false);
      alert('تم حفظ البيانات بنجاح');
    }, 500);
  };

  const [errors, setErrors] = useState({});

  const cityOptions = [
    { value: 'عمان', label: 'عمان' },
    { value: 'الزرقاء', label: 'الزرقاء' },
    { value: 'إربد', label: 'إربد' },
    { value: 'العقبة', label: 'العقبة' },
    { value: 'السلط', label: 'السلط' },
  ];

  const statusOptions = [
    { value: 'نشط', label: 'نشط' },
    { value: 'معلق', label: 'معلق' },
    { value: 'غير نشط', label: 'غير نشط' },
  ];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field) => {
    // Validate specific field on blur
    const newErrors = { ...errors };
    
    if (field === 'name' && !formData?.name?.trim()) {
      newErrors.name = 'الاسم مطلوب';
    } else if (field === 'nationalId' && !formData?.nationalId?.trim()) {
      newErrors.nationalId = 'رقم الهوية مطلوب';
    } else if (field === 'phone' && !formData?.phone?.trim()) {
      newErrors.phone = 'رقم الهاتف مطلوب';
    } else if (field === 'email') {
      if (!formData?.email?.trim()) {
        newErrors.email = 'البريد الإلكتروني مطلوب';
      } else if (!/\S+@\S+\.\S+/.test(formData?.email)) {
        newErrors.email = 'البريد الإلكتروني غير صحيح';
      }
    } else if (field === 'bankAccount' && !formData?.bankAccount?.trim()) {
      newErrors.bankAccount = 'رقم الحساب البنكي مطلوب';
    }
    
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'الاسم مطلوب';
    }
    if (!formData?.nationalId?.trim()) {
      newErrors.nationalId = 'رقم الهوية مطلوب';
    }
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'رقم الهاتف مطلوب';
    }
    if (!formData?.email?.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/\S+@\S+\.\S+/.test(formData?.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }
    if (!formData?.bankAccount?.trim()) {
      newErrors.bankAccount = 'رقم الحساب البنكي مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Add this validation check before submitting
    if (!validateForm()) {
      return;
    }
    
    onSubmit(formData);
    localStorage.removeItem('ownerFormDraft');
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={editData ? 'تعديل بيانات المالك' : 'إضافة مالك جديد'}
      size="large"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {lastSaved && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg p-3">
            <Icon name="Check" size={16} color="#16a34a" />
            <span>تم الحفظ التلقائي في {lastSaved?.toLocaleTimeString('ar-JO')}</span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="الاسم الكامل"
            type="text"
            placeholder="أدخل الاسم الكامل"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            onBlur={() => handleBlur('name')}
            error={errors?.name}
            required
          />

          <Input
            label="رقم الهوية الوطنية"
            type="text"
            placeholder="أدخل رقم الهوية"
            value={formData?.nationalId}
            onChange={(e) => handleChange('nationalId', e?.target?.value)}
            onBlur={() => handleBlur('nationalId')}
            error={errors?.nationalId}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="رقم الهاتف"
            type="tel"
            placeholder="+962 7X XXX XXXX"
            value={formData?.phone}
            onChange={(e) => handleChange('phone', e?.target?.value)}
            onBlur={() => handleBlur('phone')}
            error={errors?.phone}
            required
          />

          <Input
            label="البريد الإلكتروني"
            type="email"
            placeholder="example@domain.com"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            onBlur={() => handleBlur('email')}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="رقم الحساب البنكي"
            type="text"
            placeholder="JO XX XXXX XXXX XXXX XXXX XXXX"
            value={formData?.bankAccount}
            onChange={(e) => handleChange('bankAccount', e?.target?.value)}
            onBlur={() => handleBlur('bankAccount')}
            error={errors?.bankAccount}
            required
          />

          <Input
            label="اسم البنك"
            type="text"
            placeholder="أدخل اسم البنك"
            value={formData?.bankName}
            onChange={(e) => handleChange('bankName', e?.target?.value)}
          />
        </div>

        <Input
          label="العنوان"
          type="text"
          placeholder="أدخل العنوان الكامل"
          value={formData?.address}
          onChange={(e) => handleChange('address', e?.target?.value)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="المدينة"
            placeholder="اختر المدينة"
            options={cityOptions}
            value={formData?.city}
            onChange={(value) => handleChange('city', value)}
          />

          <Select
            label="الحالة"
            placeholder="اختر الحالة"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => handleChange('status', value)}
          />
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleSave}
            disabled={isSaving}
            iconName="Save"
            iconSize={18}
          >
            {isSaving ? 'جاري الحفظ...' : 'حفظ المسودة'}
          </Button>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              إلغاء
            </Button>
            <Button type="submit" variant="primary">
              {editData ? 'تحديث' : 'إضافة'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default OwnerIntakeForm;